import { defineStore } from 'pinia';
import { computed, ref, type Ref } from 'vue';
import handleRequest from '@/utils/helpers/handleRequest';
import Api from '@/utils/api/index.js';
import useTransform from 'lodash/transform.js';
import sortBy from 'lodash/sortBy.js';
import orderBy from 'lodash/orderBy.js';
export type TSelectRecord = Record<string | number, unknown>;

type TUserQuestionnaireSettings = {
  hide_remote: boolean;
  show_non_sustainable_preferred_transports: boolean;
  ask_off_site_work: boolean;
};

export const transformSelect = (options: TSelectRecord) =>
  useTransform(
    options,
    (acc, name, value) => {
      acc.push({ value, name });
    },
    [] as TSelectRecord[],
  );

const useGlobalStore = defineStore(
  'globalStore',
  () => {
    const loadingCount = ref(0);
    const pageKey = ref<string | number>(0);
    const countries = ref<({ name: string; value: number } | undefined)[]>([]);
    const citySubscriptions = ref<string[]>([]);
    const countriesLoading = ref(false);
    const dashboardAvailableYears = ref<number[]>([]);
    const dashboardLoading = ref(false);
    const companyAvailableYears = ref<{ name: number; value: number }[]>([]);
    const companyLoading = ref(false);
    const selectDashboardYear = ref<null | number>(null);
    const selectCompanyIdFromYear = ref(0);
    // Id of the user the remembered year choice belongs to. It is persisted
    // with the choice, so a different account (another login, or a Nova
    // impersonation in the same browser) never inherits it.
    const selectionOwnerId = ref(0);
    // Bumped whenever one user's state is cleared, so a request still in
    // flight for the previous user can't write its result back afterwards.
    const userScope = ref(0);
    const userQuestionnaireSettings = ref<TUserQuestionnaireSettings>({
      hide_remote: false,
      show_non_sustainable_preferred_transports: false,
      ask_off_site_work: false,
    });
    const userQuestionnaireInitialCountryId = ref<number | null>(null);

    const currentYearCompanyId = computed<number>(() => {
      const currentYear = new Date().getFullYear();
      return (
        companyAvailableYears.value.find(({ name }) => name === currentYear)
          ?.value ||
        companyAvailableYears.value?.at(-1)?.value ||
        0
      );
    });

    // The year a company manager is looking at: the one chosen in the menu, else the current one.
    const selectedYearCompanyId = computed<number>(
      () => selectCompanyIdFromYear.value || currentYearCompanyId.value,
    );

    const isLoading = computed(() => loadingCount.value > 0);

    const toggleLoading = (loaded: boolean) => {
      if (loaded) {
        loadingCount.value++;
      } else {
        loadingCount.value = Math.max(0, loadingCount.value - 1);
      }
    };

    const fetchData = async (
      stateRef: Ref,
      loadingRef: Ref<boolean>,
      url: string,
      // Synchronous on purpose: the scope check below and the save must run
      // in the same tick, with nothing able to clear the user in between.
      transform = (x: any) => x,
      force = false,
      userScoped = true,
    ) => {
      if (loadingRef.value || (stateRef.value.length && !force)) return;
      const scope = userScope.value;
      loadingRef.value = true;
      const { isError, data } = await handleRequest(Api.get(url));
      // A user-scoped result that arrives after that user's state was
      // cleared is dropped; clearUserState() already reset its loading flag.
      if (userScoped && scope !== userScope.value) return;
      if (!isError) stateRef.value = transform(data);
      loadingRef.value = false;
    };

    const fetchCountries = (force = false) =>
      fetchData(
        countries,
        countriesLoading,
        '/location/countries',
        transformSelect,
        force,
        false, // the same for every user, so never dropped on a switch
      );

    const setDashboardYear = (year: string | number) => {
      selectDashboardYear.value = year ? +year : null;
    };

    const setUserQuestionnaireSettings = (
      settings: TUserQuestionnaireSettings,
    ) => {
      userQuestionnaireSettings.value = settings;
    };

    const setUserQuestionnaireInitialCountryId = (id: number | null) => {
      userQuestionnaireInitialCountryId.value = id;
    };

    const setSelectCompanyIdFromYear = (id: number) => {
      selectCompanyIdFromYear.value = id;
    };

    // Everything that belongs to one signed-in user. The single place that
    // clears it, used by both reset() and claimSelectionFor().
    const clearUserState = () => {
      userScope.value++;
      citySubscriptions.value = [];
      dashboardAvailableYears.value = [];
      companyAvailableYears.value = [];
      companyLoading.value = false;
      dashboardLoading.value = false;
      selectDashboardYear.value = null;
      selectCompanyIdFromYear.value = 0;
    };

    // Called whenever the signed-in user is set. A remembered year choice
    // made by another account is dropped. An unowned choice (saved before
    // owners were tracked, or after a reset) is adopted: the year list fetch
    // still drops it if it isn't one of this user's companies.
    const claimSelectionFor = (userId: number) => {
      if (selectionOwnerId.value === userId) return;
      if (selectionOwnerId.value) clearUserState();
      selectionOwnerId.value = userId;
    };

    const fetchDashboardAvailableYears = async (force = false) => {
      await fetchData(
        dashboardAvailableYears,
        dashboardLoading,
        '/dashboard/available-years',
        d => {
          const selected = selectDashboardYear.value;
          if (!selected || !d.some(item => item === selected)) {
            setDashboardYear(d?.at(-1) || null);
          }

          return sortBy(d);
        },
        force,
      );
    };

    const fetchCompanyAvailableYears = (force = false) =>
      fetchData(
        companyAvailableYears,
        companyLoading,
        '/available-years',
        d => {
          const res = orderBy(transformSelect(d), ['name']);
          const selected = selectCompanyIdFromYear.value;
          if (!selected || !res.some(item => item.value === selected)) {
            setSelectCompanyIdFromYear((res.at(-1)?.value as number) || 0);
          }

          return res;
        },
        force,
      );

    const setCitySubscriptions = (data: string[] = []) => {
      citySubscriptions.value = data;
    };

    const updatePageKey = (key?: number | string) => {
      pageKey.value = key ? key : +pageKey.value + 1;
    };

    const reset = () => {
      loadingCount.value = 0;
      clearUserState();
      selectionOwnerId.value = 0;
    };

    return {
      isLoading,
      loadingCount,
      countries,
      citySubscriptions,
      dashboardLoading,
      companyLoading,
      countriesLoading,
      dashboardAvailableYears,
      companyAvailableYears,
      selectDashboardYear,
      selectCompanyIdFromYear,
      selectionOwnerId,
      userScope,
      pageKey,
      currentYearCompanyId,
      selectedYearCompanyId,
      userQuestionnaireInitialCountryId,
      userQuestionnaireSettings,
      setUserQuestionnaireSettings,
      setUserQuestionnaireInitialCountryId,
      updatePageKey,
      setSelectCompanyIdFromYear,
      claimSelectionFor,
      setDashboardYear,
      fetchCountries,
      fetchDashboardAvailableYears,
      fetchCompanyAvailableYears,
      transformSelect,
      setCitySubscriptions,
      toggleLoading,
      reset,
    };
  },
  {
    persist: {
      storage: localStorage,
      pick: [
        'selectCompanyIdFromYear',
        'selectDashboardYear',
        'selectionOwnerId',
      ],
    },
  },
);

export default useGlobalStore;
